import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  FileDoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../utils/axiosInstance";

const { Title, Text } = Typography;

const DEFAULT_VAT_RATE = 23;

const VAT_MODE_INCLUDED = "included";
const VAT_MODE_ADD = "add";
const VAT_MODE_NONE = "none";

const VAT_MODE_OPTIONS = [
  { value: VAT_MODE_INCLUDED, label: "VAT included" },
  { value: VAT_MODE_ADD, label: "Add VAT 23%" },
  { value: VAT_MODE_NONE, label: "No VAT" },
];

const formatCurrency = (value) => {
  const amount = Number(value || 0);

  try {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: "EUR",
    }).format(Number.isFinite(amount) ? amount : 0);
  } catch {
    return `€${Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}`;
  }
};

const formatDate = (date) => {
  if (!date) return "-";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const safeNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getOrderId = (order) => Number(order?.id || order?.order_id || 0);

const getInvoiceOrderId = (invoice) =>
  Number(invoice?.order_id || invoice?.orderId || 0);

const getInvoiceNumber = (invoice) =>
  invoice?.sage_invoice_number ||
  invoice?.invoice_number ||
  invoice?.sage_invoice_id ||
  "-";

const getOrderNumber = (order) =>
  order?.order_number ||
  order?.orderNumber ||
  order?.orderId ||
  order?.reference_number ||
  order?.referenceNumber ||
  (getOrderId(order) ? `Order-${getOrderId(order)}` : "-");

const getReferenceNumber = (order) =>
  order?.referenceNumber || order?.reference_number || getOrderNumber(order);

const getCustomerId = (item) =>
  Number(
    item?.user_id ||
      item?.userId ||
      item?.customer_id ||
      item?.customerId ||
      item?.company_id ||
      item?.companyId ||
      item?.customer?.id ||
      item?.user?.id ||
      0
  );

const getCustomerName = (item) =>
  item?.customer_name ||
  item?.company_name ||
  item?.customer?.company_name ||
  item?.customer?.full_name ||
  item?.user?.company_name ||
  item?.user?.full_name ||
  item?.full_name ||
  "-";

const getCustomerEmail = (item) =>
  item?.customer_email ||
  item?.email ||
  item?.customer?.invoice_email ||
  item?.customer?.email ||
  item?.user?.invoice_email ||
  item?.user?.email ||
  "";

const getProductName = (order) =>
  order?.product_name ||
  order?.productName ||
  order?.product?.product_name ||
  order?.product?.name ||
  order?.Product?.product_name ||
  "Order item";

const getSizeName = (order) => {
  if (typeof order?.size === "string") return order.size;

  return (
    order?.size?.size ||
    order?.size?.name ||
    order?.Size?.size ||
    order?.size_name ||
    order?.sizeName ||
    ""
  );
};

const getFabricName = (order) =>
  order?.fabric?.fabric_name ||
  order?.fabric?.name ||
  order?.Fabric?.fabric_name ||
  order?.fabric_name ||
  order?.fabricName ||
  "";

const getFabricColorName = (order) =>
  order?.fabricColor?.color_name ||
  order?.fabricColor?.name ||
  order?.FabricColor?.color_name ||
  order?.fabric_color_name ||
  order?.fabricColorName ||
  order?.color ||
  "";

const getOrderQuantity = (order) => {
  const qty = safeNumber(order?.quantity || order?.qty || 1, 1);
  return qty > 0 ? qty : 1;
};

const getOrderUnitPrice = (order) => {
  const total = safeNumber(
    order?.total_amount ??
      order?.totalAmount ??
      order?.grand_total ??
      order?.grandTotal ??
      order?.line_total ??
      order?.total_price ??
      order?.totalPrice ??
      order?.total ??
      order?.amount ??
      0
  );

  const qty = getOrderQuantity(order);

  if (total > 0 && qty > 0) {
    return Number((total / qty).toFixed(2));
  }

  return safeNumber(
    order?.unit_price ??
      order?.unitPrice ??
      order?.price ??
      order?.sale_price ??
      order?.original_price ??
      0
  );
};

const getOrderAmount = (order) => {
  const total = safeNumber(
    order?.total_amount ??
      order?.totalAmount ??
      order?.grand_total ??
      order?.grandTotal ??
      order?.line_total ??
      order?.total_price ??
      order?.totalPrice ??
      order?.total ??
      order?.amount ??
      0
  );

  if (total > 0) return total;

  return Number((getOrderQuantity(order) * getOrderUnitPrice(order)).toFixed(2));
};

const getDeliveryStatus = (order) =>
  order?.delivery_status ||
  order?.deliveryStatus ||
  order?.delivery_status_name ||
  order?.deliveryStatusName ||
  order?.dispatchStatus ||
  order?.dispatch_status ||
  order?.status ||
  order?.order_status ||
  order?.orderStatus ||
  order?.productionStatus ||
  order?.production_status ||
  order?.sheetProductionStatus ||
  order?.sheet_production_status ||
  "";

const isDeliveredOrder = (order) => {
  if (order?.is_delivered_for_invoice === true) return true;

  const value = String(getDeliveryStatus(order) || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");

  return (
    value === "delivered" ||
    value === "delivery complete" ||
    value === "delivery completed" ||
    value === "shipped" ||
    value.includes("delivered")
  );
};

const buildOrderDescription = (order) => {
  const parts = [
    getProductName(order),
    getSizeName(order),
    order?.storageType || order?.storage_type || "",
    getFabricName(order),
    getFabricColorName(order),
    getReferenceNumber(order) ? `Ref: ${getReferenceNumber(order)}` : "",
  ].filter(Boolean);

  return parts.length ? parts.join(" | ") : `Order #${getOrderId(order)}`;
};

const makeOrderLine = (order) => ({
  lineType: "order",
  orderId: getOrderId(order),
  description: buildOrderDescription(order),
  quantity: getOrderQuantity(order),
  unitPrice: getOrderUnitPrice(order),
  vatMode: VAT_MODE_INCLUDED,
  taxRate: DEFAULT_VAT_RATE,
});

const makeManualLine = () => ({
  lineType: "manual",
  orderId: null,
  description: "",
  quantity: 1,
  unitPrice: 0,
  vatMode: VAT_MODE_ADD,
  taxRate: DEFAULT_VAT_RATE,
});

const getLineNetAmount = (line) =>
  safeNumber(line.quantity, 0) * safeNumber(line.unitPrice, 0);

const getLineVatAmount = (line) => {
  const base = getLineNetAmount(line);
  const vatMode = line?.vatMode || VAT_MODE_INCLUDED;
  const taxRate = safeNumber(line?.taxRate, 0);

  if (vatMode !== VAT_MODE_ADD || taxRate <= 0) return 0;

  return (base * taxRate) / 100;
};

const getLineTotalAmount = (line) =>
  getLineNetAmount(line) + getLineVatAmount(line);

export default function CustomerInvoicesAdmin() {
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState("non-invoiced");

  const [searchText, setSearchText] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState("all");

  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [invoiceForm] = Form.useForm();

  const loadOrders = useCallback(async () => {
    try {
      setLoadingOrders(true);

      const response = await axiosInstance.get(
        "/sage-invoices/admin/orders?status=all&deliveredOnly=true&limit=1000"
      );

      const list = Array.isArray(response.data?.orders)
        ? response.data.orders
        : [];

      setOrders(list.filter(isDeliveredOrder));
    } catch (error) {
      console.error("load admin invoice orders error:", error);
      message.error(
        error?.response?.data?.message ||
          "Failed to load orders for Sage invoices."
      );
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const loadInvoices = useCallback(async () => {
    try {
      setLoadingInvoices(true);

      const response = await axiosInstance.get(
        "/sage-invoices/admin/invoices?limit=1000"
      );

      const list = Array.isArray(response.data?.invoices)
        ? response.data.invoices
        : [];

      setInvoices(list);
    } catch (error) {
      console.error("load admin Sage invoices error:", error);
      message.error(
        error?.response?.data?.message || "Failed to load Sage invoices."
      );
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadOrders(), loadInvoices()]);
  }, [loadOrders, loadInvoices]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const invoiceOrderIdSet = useMemo(() => {
    return new Set(
      invoices
        .map((invoice) => getInvoiceOrderId(invoice))
        .filter((id) => Number.isInteger(id) && id > 0)
    );
  }, [invoices]);

  const hasSageInvoice = useCallback(
    (order) => {
      const orderId = getOrderId(order);

      return (
        !!order?.has_sage_invoice ||
        !!order?.sage_invoice ||
        invoiceOrderIdSet.has(orderId)
      );
    },
    [invoiceOrderIdSet]
  );

  const companies = useMemo(() => {
    const map = new Map();

    orders.forEach((order) => {
      const id = getCustomerId(order);
      if (!id) return;

      if (!map.has(id)) {
        map.set(id, {
          id,
          name: getCustomerName(order),
          email: getCustomerEmail(order),
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name))
    );
  }, [orders]);

  const companyAndSearchFilteredOrders = useMemo(() => {
    const value = searchText.trim().toLowerCase();

    return orders.filter((order) => {
      if (!isDeliveredOrder(order)) return false;

      if (selectedCompanyId !== "all") {
        if (String(getCustomerId(order)) !== String(selectedCompanyId)) {
          return false;
        }
      }

      if (!value) return true;

      const haystack = [
        getOrderNumber(order),
        getReferenceNumber(order),
        getCustomerName(order),
        getCustomerEmail(order),
        getProductName(order),
        getSizeName(order),
        getFabricName(order),
        getFabricColorName(order),
        getDeliveryStatus(order),
        order?.status,
        getOrderId(order),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(value);
    });
  }, [orders, selectedCompanyId, searchText]);

  const companyAndSearchFilteredInvoices = useMemo(() => {
    const value = searchText.trim().toLowerCase();

    return invoices.filter((invoice) => {
      if (selectedCompanyId !== "all") {
        const invoiceUserId = Number(
          invoice?.user_id || invoice?.customer_id || 0
        );

        if (
          invoiceUserId &&
          String(invoiceUserId) !== String(selectedCompanyId)
        ) {
          return false;
        }
      }

      if (!value) return true;

      const haystack = [
        getInvoiceNumber(invoice),
        invoice?.order_id,
        invoice?.customer_name,
        invoice?.customer_email,
        invoice?.status,
        invoice?.payment_status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(value);
    });
  }, [invoices, searchText, selectedCompanyId]);

  const visibleNonInvoicedOrders = useMemo(() => {
    return companyAndSearchFilteredOrders.filter((order) => !hasSageInvoice(order));
  }, [companyAndSearchFilteredOrders, hasSageInvoice]);

  const visibleInvoicedOrders = useMemo(() => {
    return companyAndSearchFilteredOrders.filter((order) => hasSageInvoice(order));
  }, [companyAndSearchFilteredOrders, hasSageInvoice]);

  const tableOrders = useMemo(() => {
    if (activeTab === "invoiced") return visibleInvoicedOrders;
    return visibleNonInvoicedOrders;
  }, [activeTab, visibleInvoicedOrders, visibleNonInvoicedOrders]);

  const selectedOrders = useMemo(() => {
    const selectedSet = new Set(selectedRowKeys.map(Number));

    return visibleNonInvoicedOrders.filter((order) =>
      selectedSet.has(getOrderId(order))
    );
  }, [visibleNonInvoicedOrders, selectedRowKeys]);

  const selectedCustomerId = useMemo(() => {
    if (!selectedOrders.length) return null;
    return getCustomerId(selectedOrders[0]);
  }, [selectedOrders]);

  const selectedCompanyMismatch = useMemo(() => {
    if (!selectedOrders.length) return false;

    const firstCustomerId = getCustomerId(selectedOrders[0]);

    return selectedOrders.some(
      (order) => getCustomerId(order) !== firstCustomerId
    );
  }, [selectedOrders]);

  const totals = useMemo(() => {
    const subtotal = invoiceLines.reduce(
      (sum, line) => sum + getLineNetAmount(line),
      0
    );

    const extraVat = invoiceLines.reduce(
      (sum, line) => sum + getLineVatAmount(line),
      0
    );

    const grandTotal = subtotal + extraVat;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      extraVat: Number(extraVat.toFixed(2)),
      grandTotal: Number(grandTotal.toFixed(2)),
    };
  }, [invoiceLines]);

  const resetSelection = () => {
    setSelectedRowKeys([]);
    setInvoiceLines([]);
    invoiceForm.resetFields();
  };

  const prepareInvoiceModalForOrders = (ordersToInvoice) => {
    const cleanOrders = Array.isArray(ordersToInvoice)
      ? ordersToInvoice.filter(Boolean)
      : [];

    if (!cleanOrders.length) {
      message.warning("Please select at least one delivered, non-invoiced order.");
      return;
    }

    if (cleanOrders.some((order) => !isDeliveredOrder(order))) {
      message.warning("Only delivered orders can be sent to Sage customer invoice.");
      return;
    }

    if (cleanOrders.some((order) => hasSageInvoice(order))) {
      message.warning("Already invoiced orders cannot be selected again.");
      return;
    }

    const firstCustomer = getCustomerId(cleanOrders[0]);

    const mismatch = cleanOrders.some(
      (order) => getCustomerId(order) !== firstCustomer
    );

    if (mismatch) {
      message.warning("Please select orders from one company only.");
      return;
    }

    setInvoiceLines(cleanOrders.map(makeOrderLine));

    invoiceForm.setFieldsValue({
      issueDate: dayjs(),
      dueDate: null,
      reference: cleanOrders.map(getReferenceNumber).filter(Boolean).join(", "),
      notes: "",
    });

    setInvoiceModalOpen(true);
  };

  const openInvoiceModal = () => {
    prepareInvoiceModalForOrders(selectedOrders);
  };

  const addManualLine = () => {
    setInvoiceLines((prev) => [...prev, makeManualLine()]);
  };

  const updateLine = (index, patch) => {
    setInvoiceLines((prev) => {
      const copy = [...prev];

      copy[index] = {
        ...copy[index],
        ...patch,
      };

      if (patch.vatMode === VAT_MODE_INCLUDED) {
        copy[index].taxRate = DEFAULT_VAT_RATE;
      }

      if (patch.vatMode === VAT_MODE_ADD) {
        copy[index].taxRate = DEFAULT_VAT_RATE;
      }

      if (patch.vatMode === VAT_MODE_NONE) {
        copy[index].taxRate = 0;
      }

      return copy;
    });
  };

  const removeLine = (index) => {
    setInvoiceLines((prev) =>
      prev.filter((_, lineIndex) => lineIndex !== index)
    );
  };

  const generateCombinedSageInvoice = async () => {
    try {
      if (!selectedOrders.length) {
        message.warning("Please select at least one order.");
        return;
      }

      if (selectedCompanyMismatch) {
        message.warning("Please select orders from one company only.");
        return;
      }

      if (selectedOrders.some((order) => hasSageInvoice(order))) {
        message.warning("Already invoiced orders cannot be generated again.");
        return;
      }

      const values = await invoiceForm.validateFields();

      const cleanLines = invoiceLines
        .map((line) => ({
          lineType: line.lineType || "manual",
          orderId: line.orderId || null,
          description: String(line.description || "").trim(),
          quantity: safeNumber(line.quantity, 1),
          unitPrice: safeNumber(line.unitPrice, 0),
          vatMode: line.vatMode || VAT_MODE_INCLUDED,
          taxRate:
            line.vatMode === VAT_MODE_NONE
              ? 0
              : safeNumber(line.taxRate, DEFAULT_VAT_RATE),
        }))
        .filter(
          (line) => line.description && line.quantity > 0 && line.unitPrice >= 0
        );

      if (!cleanLines.length) {
        message.warning("Please add at least one valid invoice line.");
        return;
      }

      const orderIds = selectedOrders.map(getOrderId).filter(Boolean);

      const alreadyInvoicedOrderIds = orderIds.filter((orderId) =>
        invoiceOrderIdSet.has(Number(orderId))
      );

      if (alreadyInvoicedOrderIds.length) {
        message.error(
          `These orders already have Sage invoices: ${alreadyInvoicedOrderIds.join(
            ", "
          )}`
        );
        await refreshAll();
        return;
      }

      Modal.confirm({
        title: "Generate Sage invoice?",
        content:
          selectedOrders.length > 1
            ? `This will create one Sage invoice for ${selectedOrders.length} selected orders.`
            : "This will create one Sage invoice for the selected order.",
        okText: "Generate Sage Invoice",
        cancelText: "Cancel",
        async onOk() {
          try {
            setGenerating(true);

            await axiosInstance.post("/sage-invoices/admin/generate-combined", {
              userId: selectedCustomerId,
              orderIds,
              lines: cleanLines,
              reference: values.reference || "",
              notes: values.notes || "",
              issueDate: values.issueDate
                ? values.issueDate.format("YYYY-MM-DD")
                : dayjs().format("YYYY-MM-DD"),
              dueDate: values.dueDate
                ? values.dueDate.format("YYYY-MM-DD")
                : null,
            });

            message.success("Sage invoice generated successfully.");

            setInvoiceModalOpen(false);
            resetSelection();

            await refreshAll();
            setActiveTab("invoiced");
          } catch (error) {
            console.error("generate combined Sage invoice error:", error);
            message.error(
              error?.response?.data?.message ||
                "Failed to generate Sage invoice."
            );
          } finally {
            setGenerating(false);
          }
        },
      });
    } catch (error) {
      if (error?.errorFields) return;

      console.error("invoice form error:", error);
      message.error(error?.response?.data?.message || "Please check invoice data.");
    }
  };

  const rowSelection = {
    selectedRowKeys,
    preserveSelectedRowKeys: false,
    getCheckboxProps: (order) => ({
      disabled: !isDeliveredOrder(order) || hasSageInvoice(order),
    }),
    onChange: (keys) => {
      const validKeys = keys
        .map(Number)
        .filter((id) =>
          visibleNonInvoicedOrders.some(
            (order) =>
              getOrderId(order) === id &&
              isDeliveredOrder(order) &&
              !hasSageInvoice(order)
          )
        );

      setSelectedRowKeys(validKeys);
    },
  };

  const renderDeliveryStatusTag = (status) => {
    const text = String(status || "-");
    const lower = text.toLowerCase();

    let color = "default";

    if (
      lower.includes("deliver") ||
      lower.includes("ship") ||
      lower.includes("dispatch")
    ) {
      color = "green";
    }

    return <Tag color={color}>{text}</Tag>;
  };

  const orderColumns = [
    {
      title: "Order",
      key: "order",
      render: (_, order) => (
        <div>
          <Text strong>{getOrderNumber(order)}</Text>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            ID: {getOrderId(order)}
          </div>
        </div>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, order) => (
        <div>
          <Text strong>{getCustomerName(order)}</Text>
          {getCustomerEmail(order) ? (
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {getCustomerEmail(order)}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Order Date",
      key: "createdAt",
      render: (_, order) =>
        formatDate(order?.createdAt || order?.created_at || order?.orderDate),
      responsive: ["md"],
    },
    {
      title: "Delivery Status",
      key: "delivery_status",
      render: (_, order) => renderDeliveryStatusTag(getDeliveryStatus(order)),
      responsive: ["md"],
    },
    {
      title: "Amount",
      key: "amount",
      render: (_, order) => formatCurrency(getOrderAmount(order)),
      responsive: ["md"],
    },
    {
      title: "Invoice Status",
      key: "invoice_status",
      render: (_, order) =>
        hasSageInvoice(order) ? (
          <Tag color="green">Invoice Generated</Tag>
        ) : (
          <Tag color="orange">Not Invoiced</Tag>
        ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, order) => {
        if (hasSageInvoice(order)) {
          return <Text type="secondary">Already invoiced</Text>;
        }

        return (
          <Button
            type="link"
            disabled={!isDeliveredOrder(order) || hasSageInvoice(order)}
            onClick={() => {
              const orderId = getOrderId(order);
              setSelectedRowKeys([orderId]);
              prepareInvoiceModalForOrders([order]);
            }}
          >
            Prepare Invoice
          </Button>
        );
      },
    },
  ];

  const invoiceColumns = [
    {
      title: "Invoice",
      key: "invoice",
      render: (_, invoice) => (
        <div>
          <Text strong>{getInvoiceNumber(invoice)}</Text>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            Order #{invoice?.order_id || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, invoice) => (
        <div>
          <Text strong>{getCustomerName(invoice)}</Text>
          {getCustomerEmail(invoice) ? (
            <div style={{ fontSize: 12, color: "#64748b" }}>
              {getCustomerEmail(invoice)}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) => formatCurrency(amount),
    },
    {
      title: "Status",
      key: "status",
      render: (_, invoice) => {
        const status = invoice?.status || invoice?.payment_status || "Unpaid";
        const lower = String(status).toLowerCase();

        let color = "blue";
        if (lower === "paid") color = "green";
        if (lower.includes("overdue")) color = "red";
        if (lower.includes("partial")) color = "orange";
        if (lower.includes("void") || lower.includes("cancel")) color = "default";

        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date),
      responsive: ["md"],
    },
  ];

  const lineColumns = [
    {
      title: "Type",
      key: "lineType",
      width: 110,
      render: (_, line) =>
        line.lineType === "order" ? (
          <Tag color="blue">Order</Tag>
        ) : (
          <Tag color="purple">Manual</Tag>
        ),
    },
    {
      title: "Description",
      key: "description",
      render: (_, line, index) => (
        <Input
          value={line.description}
          placeholder="Description"
          onChange={(event) =>
            updateLine(index, { description: event.target.value })
          }
        />
      ),
    },
    {
      title: "Qty",
      key: "quantity",
      width: 100,
      render: (_, line, index) => (
        <InputNumber
          min={1}
          value={line.quantity}
          onChange={(value) => updateLine(index, { quantity: value || 1 })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Unit Price",
      key: "unitPrice",
      width: 140,
      render: (_, line, index) => (
        <InputNumber
          min={0}
          value={line.unitPrice}
          onChange={(value) => updateLine(index, { unitPrice: value || 0 })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "VAT",
      key: "vatMode",
      width: 170,
      render: (_, line, index) => (
        <Select
          value={line.vatMode || VAT_MODE_INCLUDED}
          style={{ width: "100%" }}
          onChange={(value) =>
            updateLine(index, {
              vatMode: value,
              taxRate: value === VAT_MODE_NONE ? 0 : DEFAULT_VAT_RATE,
            })
          }
          options={VAT_MODE_OPTIONS}
        />
      ),
    },
    {
      title: "VAT %",
      key: "taxRate",
      width: 110,
      render: (_, line, index) => (
        <InputNumber
          min={0}
          max={100}
          value={safeNumber(line.taxRate, DEFAULT_VAT_RATE)}
          disabled={(line.vatMode || VAT_MODE_INCLUDED) === VAT_MODE_NONE}
          formatter={(value) => `${value}%`}
          parser={(value) => String(value || "").replace("%", "")}
          onChange={(value) => updateLine(index, { taxRate: value || 0 })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Line Total",
      key: "lineTotal",
      width: 160,
      render: (_, line) => {
        const total = getLineTotalAmount(line);
        const vatAmount = getLineVatAmount(line);

        return (
          <div>
            <Text strong>{formatCurrency(total)}</Text>
            {vatAmount > 0 ? (
              <div style={{ fontSize: 11, color: "#64748b" }}>
                + VAT {formatCurrency(vatAmount)}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "",
      key: "delete",
      width: 64,
      render: (_, line, index) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          disabled={invoiceLines.length <= 1 && line.lineType === "order"}
          onClick={() => removeLine(index)}
        />
      ),
    },
  ];

  return (
    <div className="sage-invoice-page">
      <style>{`
        .sage-invoice-page {
          padding: 22px;
          background: #f6f8fc;
          min-height: calc(100vh - 72px);
        }

        .sage-invoice-header {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          align-items: flex-start;
          margin-bottom: 18px;
        }

        .sage-invoice-header-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .sage-stat-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
          margin-bottom: 16px;
        }

        .sage-stat-card,
        .sage-table-card {
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
        }

        .sage-toolbar {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          align-items: center;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }

        .sage-invoice-modal .ant-modal-content {
          border-radius: 22px;
          overflow: hidden;
          padding: 0;
        }

        .sage-invoice-modal .ant-modal-header {
          padding: 22px 26px 10px;
          margin: 0;
          border-bottom: 0;
        }

        .sage-invoice-modal .ant-modal-body {
          padding: 12px 26px 22px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
        }

        .sage-invoice-modal .ant-modal-footer {
          padding: 16px 26px 22px;
          margin: 0;
          background: #f8fafc;
          border-top: 1px solid #e5e7eb;
        }

        .sage-modal-hero {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          padding: 16px 18px;
          border-radius: 18px;
          background: #eef6ff;
          border: 1px solid #dbeafe;
          margin-bottom: 14px;
        }

        .sage-modal-hero-title {
          font-size: 18px;
          font-weight: 800;
          color: #0f172a;
        }

        .sage-modal-hero-subtitle {
          font-size: 13px;
          color: #475569;
          margin-top: 2px;
        }

        .sage-vat-note {
          margin-bottom: 14px;
          padding: 12px 14px;
          border-radius: 14px;
          background: #fff7ed;
          border: 1px solid #fed7aa;
          color: #9a3412;
          font-size: 13px;
        }

        .sage-lines-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          padding: 12px;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
        }

        .sage-modal-total-row {
          display: flex;
          justify-content: flex-end;
          margin-top: 16px;
        }

        .sage-total-box {
          min-width: 280px;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          background: #ffffff;
          padding: 14px 16px;
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
        }

        .sage-total-line {
          display: flex;
          justify-content: space-between;
          gap: 20px;
          align-items: center;
          color: #475569;
          margin-bottom: 8px;
        }

        .sage-grand-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
          padding-top: 10px;
          border-top: 1px solid #e2e8f0;
          font-size: 20px;
          font-weight: 900;
          color: #0f172a;
        }

        @media (max-width: 768px) {
          .sage-invoice-page {
            padding: 14px;
          }

          .sage-invoice-header {
            flex-direction: column;
          }

          .sage-invoice-header-actions {
            width: 100%;
            justify-content: flex-start;
          }

          .sage-stat-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="sage-invoice-header">
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            Customer Invoices
          </Title>
          <Text type="secondary">
            Select non-invoiced delivered orders and generate Sage invoices.
          </Text>
        </div>

        <div className="sage-invoice-header-actions">
          <Select
            showSearch
            value={selectedCompanyId}
            onChange={(value) => {
              setSelectedCompanyId(value);
              setSelectedRowKeys([]);
            }}
            optionFilterProp="label"
            style={{ width: 260 }}
            options={[
              { value: "all", label: "All companies" },
              ...companies.map((company) => ({
                value: String(company.id),
                label: `${company.name}${
                  company.email ? ` (${company.email})` : ""
                }`,
              })),
            ]}
          />

          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search order, customer, invoice..."
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
              setSelectedRowKeys([]);
            }}
            style={{ width: 280 }}
          />

          <Button
            icon={<ReloadOutlined />}
            onClick={refreshAll}
            loading={loadingOrders || loadingInvoices}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="sage-stat-grid">
        <Card className="sage-stat-card">
          <Text type="secondary">Non-invoiced orders</Text>
          <Title level={3} style={{ margin: "8px 0 0" }}>
            {visibleNonInvoicedOrders.length}
          </Title>
        </Card>

        <Card className="sage-stat-card">
          <Text type="secondary">Invoiced orders</Text>
          <Title level={3} style={{ margin: "8px 0 0" }}>
            {visibleInvoicedOrders.length}
          </Title>
        </Card>

        <Card className="sage-stat-card">
          <Text type="secondary">Sage invoice records</Text>
          <Title level={3} style={{ margin: "8px 0 0" }}>
            {companyAndSearchFilteredInvoices.length}
          </Title>
        </Card>
      </div>

      <Card className="sage-table-card">
        <div className="sage-toolbar">
          <Space wrap>
            <Text strong>Selected orders: {selectedRowKeys.length}</Text>

            {selectedOrders.length ? (
              <Text type="secondary">{getCustomerName(selectedOrders[0])}</Text>
            ) : null}

            {selectedCompanyMismatch ? (
              <Tag color="red">Please select one company only</Tag>
            ) : null}
          </Space>

          <Space wrap>
            <Button onClick={resetSelection} disabled={!selectedRowKeys.length}>
              Clear Selection
            </Button>

            <Button
              type="primary"
              icon={<FileDoneOutlined />}
              disabled={!selectedRowKeys.length || selectedCompanyMismatch}
              onClick={openInvoiceModal}
            >
              Send to Invoice
            </Button>
          </Space>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setSelectedRowKeys([]);
          }}
          items={[
            {
              key: "non-invoiced",
              label: `Non-Invoiced Orders (${visibleNonInvoicedOrders.length})`,
              children: (
                <Table
                  rowKey={(row) => getOrderId(row)}
                  rowSelection={rowSelection}
                  columns={orderColumns}
                  dataSource={tableOrders}
                  loading={loadingOrders}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                  }}
                  scroll={{ x: 980 }}
                />
              ),
            },
            {
              key: "invoiced",
              label: `Invoiced Orders (${visibleInvoicedOrders.length})`,
              children: (
                <Table
                  rowKey={(row) => getOrderId(row)}
                  columns={orderColumns}
                  dataSource={tableOrders}
                  loading={loadingOrders}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                  }}
                  scroll={{ x: 980 }}
                />
              ),
            },
            {
              key: "sage-invoices",
              label: `Sage Invoices (${companyAndSearchFilteredInvoices.length})`,
              children: (
                <Table
                  rowKey={(row) => row.id || row.sage_invoice_id}
                  columns={invoiceColumns}
                  dataSource={companyAndSearchFilteredInvoices}
                  loading={loadingInvoices}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                  }}
                  scroll={{ x: 900 }}
                />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        open={invoiceModalOpen}
        title="Create Sage Customer Invoice"
        className="sage-invoice-modal"
        width={1180}
        onCancel={() => setInvoiceModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setInvoiceModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="manual" icon={<PlusOutlined />} onClick={addManualLine}>
            Add Manual Entry
          </Button>,
          <Button
            key="generate"
            type="primary"
            icon={<FileDoneOutlined />}
            loading={generating}
            onClick={generateCombinedSageInvoice}
          >
            Generate Sage Invoice
          </Button>,
        ]}
      >
        <div className="sage-modal-hero">
          <div>
            <div className="sage-modal-hero-title">
              {selectedOrders[0]
                ? getCustomerName(selectedOrders[0])
                : "Selected customer"}
            </div>

            <div className="sage-modal-hero-subtitle">
              {selectedOrders.length} order
              {selectedOrders.length === 1 ? "" : "s"} selected
              {selectedOrders[0] && getCustomerEmail(selectedOrders[0])
                ? ` • ${getCustomerEmail(selectedOrders[0])}`
                : ""}
            </div>
          </div>

          <Tag color="blue" style={{ borderRadius: 999, padding: "4px 12px" }}>
            Sage Invoice
          </Tag>
        </div>

        <div className="sage-vat-note">
          Order lines use “VAT included” by default because customer order prices
          already include VAT. Manual entries use “Add VAT 23%” by default.
        </div>

        <Form form={invoiceForm} layout="vertical">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 14,
            }}
          >
            <Form.Item label="Company">
              <Input
                value={
                  selectedOrders[0] ? getCustomerName(selectedOrders[0]) : ""
                }
                disabled
              />
            </Form.Item>

            <Form.Item
              label="Invoice Date"
              name="issueDate"
              rules={[{ required: true, message: "Invoice date is required" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item label="Due Date" name="dueDate">
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item label="Reference / PO Number" name="reference">
              <Input placeholder="Reference / PO number" />
            </Form.Item>
          </div>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={2} placeholder="Optional invoice notes" />
          </Form.Item>
        </Form>

        <div className="sage-lines-card">
          <Table
            rowKey={(_, index) => index}
            columns={lineColumns}
            dataSource={invoiceLines}
            pagination={false}
            scroll={{ x: 900 }}
          />
        </div>

        <div className="sage-modal-total-row">
          <div className="sage-total-box">
            <div className="sage-total-line">
              <span>Subtotal</span>
              <b>{formatCurrency(totals.subtotal)}</b>
            </div>

            <div className="sage-total-line">
              <span>Extra VAT added</span>
              <b>{formatCurrency(totals.extraVat)}</b>
            </div>

            <div className="sage-grand-total">
              <span>Total</span>
              <span>{formatCurrency(totals.grandTotal)}</span>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}